if (!customElements.get('mobile-dock')) {
  customElements.define(
    'mobile-dock',
    class MobileDock extends HTMLElement {
      constructor() {
        super();
        this.cleanupCallbacks = [];
      }

      connectedCallback() {
        if (Shopify.designMode) {
          this.init();
        }
        else {
          this.deferredInit = new theme.initWhenVisible(
            theme.utils.throttle(() => {
              if (this.isConnected) this.init();
              this.deferredInit = null;
            })
          );
        }
      }

      disconnectedCallback() {
        this.deferredInit?.cleanup();
        this.deferredInit = null;
        this.cleanupCallbacks.splice(0).forEach((cleanup) => cleanup());
        this.initialized = false;
        this._section = null;
        this.observedInboxHost = null;
      }

      on(target, eventName, listener, options) {
        if (!target) return;
        target.addEventListener(eventName, listener, options);
        this.registerCleanup(() => target.removeEventListener(eventName, listener, options));
      }

      registerCleanup(cleanup) {
        this.cleanupCallbacks.push(cleanup);
      }

      get section() {
        return this._section = this._section || this.closest('.mobile-dock-section');
      }

      init() {
        if (this.initialized) return;
        this.initialized = true;
        this.setAttribute('loaded', '');

        this.detectForHeader();
        this.detectForFooter();
        this.initShopifyInbox();
        setTimeout(this.setHeight.bind(this));
        this.on(document, 'matchSmall', this.setHeight.bind(this));

        if (Shopify.designMode) {
          this.on(this.section, 'shopify:section:select', () => {
            this.section.classList.add('shopify-active');
          });
          this.on(this.section, 'shopify:section:deselect', () => {
            this.section.classList.remove('shopify-active');
          });
        }

        this.on(this, 'click', this.onDockClick.bind(this));
      }

      onDockClick(event) {
        const trigger = event.target.closest('[data-chat-trigger]');
        if (!trigger || !this.contains(trigger)) return;

        event.preventDefault();
        event.stopPropagation();
        this.openShopifyInbox(trigger);
      }

      initShopifyInbox() {
        this.syncShopifyInboxState();

        this.inboxDocumentObserver = new MutationObserver(() => {
          this.syncShopifyInboxState();
        });
        this.inboxDocumentObserver.observe(document.documentElement, {
          childList: true,
          subtree: true
        });

        this.registerCleanup(() => {
          this.inboxDocumentObserver?.disconnect();
          this.inboxHostObserver?.disconnect();
          this.inboxRootObserver?.disconnect();
          document.documentElement.classList.remove('porphis-inbox-open');
        });
      }

      getShopifyInboxHost() {
        return document.querySelector(
          'inbox-online-store-chat#ShopifyChat, inbox-online-store-chat, #ShopifyChat'
        );
      }

      queryShadowTree(root, selector) {
        if (!root?.querySelector) return null;

        const directMatch = root.querySelector(selector);
        if (directMatch) return directMatch;

        const elements = root.querySelectorAll('*');
        for (const element of elements) {
          if (!element.shadowRoot) continue;
          const nestedMatch = this.queryShadowTree(element.shadowRoot, selector);
          if (nestedMatch) return nestedMatch;
        }

        return null;
      }

      getButtonFromFrame(frame) {
        if (!frame?.contentWindow) return null;

        try {
          return frame.contentWindow.document.querySelector(
            'button.chat-toggle, button[aria-controls="chat-ui"], button[aria-label*="chat" i], button'
          );
        }
        catch (error) {
          return null;
        }
      }

      getShopifyInboxButton() {
        const host = this.getShopifyInboxHost();
        const buttonSelector = [
          'button.chat-toggle',
          '.chat-app > button',
          'button[aria-controls="chat-ui"]',
          'button[aria-label*="chat" i]'
        ].join(',');

        if (host) {
          if (host.matches?.(buttonSelector)) return host;

          const root = host.shadowRoot || host;
          const button = this.queryShadowTree(root, buttonSelector);
          if (button) return button;

          if (host.tagName === 'IFRAME') {
            const frameButton = this.getButtonFromFrame(host);
            if (frameButton) return frameButton;
          }
        }

        const legacyButton = document.querySelector(
          '#shopify-element-wrapper .activator, #shopify-element-wrapper .chat-toggle, #shopify-chat-dummy'
        );
        if (legacyButton) return legacyButton;

        const legacyFrame = document.querySelector('#dummy-chat-button-iframe');
        return this.getButtonFromFrame(legacyFrame) || legacyFrame;
      }

      isShopifyInboxOpen(host = this.getShopifyInboxHost()) {
        if (!host) return false;

        const isOpen = host.getAttribute('is-open');
        if (isOpen === 'true') return true;
        if (isOpen === 'false') return false;

        const root = host.shadowRoot || host;
        return Boolean(this.queryShadowTree(root, [
          'button[aria-expanded="true"]',
          '[role="dialog"]:not([hidden])',
          '.chat-app--open',
          '.chat-ui:not([hidden])'
        ].join(',')));
      }

      observeShopifyInboxHost(host) {
        if (!host || this.observedInboxHost === host) return;

        this.inboxHostObserver?.disconnect();
        this.inboxRootObserver?.disconnect();
        this.observedInboxHost = host;

        this.inboxHostObserver = new MutationObserver(() => {
          this.syncShopifyInboxState();
        });
        this.inboxHostObserver.observe(host, {
          attributes: true,
          childList: true,
          subtree: true,
          attributeFilter: ['is-open', 'open', 'aria-expanded', 'class']
        });

        if (host.shadowRoot) {
          this.inboxRootObserver = new MutationObserver(() => {
            this.syncShopifyInboxState();
          });
          this.inboxRootObserver.observe(host.shadowRoot, {
            attributes: true,
            childList: true,
            subtree: true,
            attributeFilter: ['open', 'aria-expanded', 'class', 'hidden']
          });
        }
      }

      syncShopifyInboxState() {
        const host = this.getShopifyInboxHost();
        const isOpen = this.isShopifyInboxOpen(host);

        if (host) {
          host.classList.add('porphis-inbox-managed');
          host.classList.toggle('porphis-inbox-open', isOpen);
          host.classList.toggle('porphis-inbox-closed', !isOpen);
          this.observeShopifyInboxHost(host);
        }

        document.documentElement.classList.toggle('porphis-inbox-open', isOpen);
        this.querySelectorAll('[data-chat-trigger]').forEach((trigger) => {
          trigger.setAttribute('aria-expanded', String(isOpen));
          if (isOpen) trigger.removeAttribute('aria-busy');
        });

        return isOpen;
      }

      openShopifyInbox(trigger, attempt = 0) {
        if (this.syncShopifyInboxState()) return;

        const button = this.getShopifyInboxButton();
        if (button) {
          trigger?.setAttribute('aria-busy', 'true');
          button.click();

          window.setTimeout(() => {
            this.syncShopifyInboxState();
            trigger?.removeAttribute('aria-busy');
          }, 300);
          return;
        }

        if (attempt < 40) {
          trigger?.setAttribute('aria-busy', 'true');
          window.setTimeout(() => {
            this.openShopifyInbox(trigger, attempt + 1);
          }, 150);
          return;
        }

        trigger?.removeAttribute('aria-busy');
        console.warn('Shopify Inbox is enabled but its storefront button could not be found.');
      }

      detectForHeader() {
        const header = document.querySelector('.header-section');
        if (header === null) {
          this.section.classList.add('active');
          return;
        }

        if (!header.classList.contains('header-sticky')) {
          this.scrollY = parseInt(header.getBoundingClientRect().bottom + window.scrollY);
          const onScroll = theme.utils.throttle(this.onScrollForHeader.bind(this));
          this.on(window, 'scroll', onScroll, { passive: true });
          this.registerCleanup(() => onScroll.cancel());
        }
      }

      onScrollForHeader() {
        if (window.scrollY >= this.scrollY) {
          this.section.classList.add('active');
        }
        else {
          this.section.classList.remove('active');
        }
      }

      detectForFooter() {
        const footer = document.querySelector('.footer-copyright');
        if (footer === null) return;

        const onScroll = theme.utils.throttle(this.onScrollForFooter.bind(this));
        this.on(window, 'scroll', onScroll, { passive: true });
        this.registerCleanup(() => onScroll.cancel());
      }

      onScrollForFooter() {
        if (!theme.config.mqlSmall) return;

        const scrolledTo = window.scrollY + window.innerHeight;
        const threshold = this.offsetHeight;
        const isReachBottom = document.body.scrollHeight - threshold <= scrolledTo;
        this.classList.toggle('active', isReachBottom);
      }

      setHeight() {
        document.documentElement.style.setProperty('--mobile-dock-height', `${this.getBoundingClientRect().height.toFixed(1)}px`);
      }
    }, { extends: 'nav' }
  );
}
